import React from 'react'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import mainContext from '../../contexts/mainContext'
import { updateOfficialAccount } from '../../Controllers/userManagment/common'
import { __snackBarSeverity } from '../../constants'

// Pre-filled when an account is first marked official, editable before saving
const DEFAULT_CONTACT_EMAIL = 'hello@nonaglobal.com'
const DEFAULT_WEBSITE = 'www.nonaglobal.com'

const switchSx = {
    // The app's global `* { box-sizing: unset !important; }` reset (src/css/base.css)
    // breaks MUI Switch's internal thumb/track sizing — restore border-box here.
    boxSizing: 'border-box !important',
    '& *': { boxSizing: 'border-box !important' },
    '& .MuiSwitch-switchBase.Mui-checked': { color: 'var(--nona-theme-color)' },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'var(--nona-theme-color)' },
}

const OfficialAccount = ({ userId, isOfficial, contactEmail, website, onSaved }) => {
    const { setSnackBar } = React.useContext(mainContext)
    const [official, setOfficial] = React.useState(!!isOfficial)
    const [email, setEmail] = React.useState(contactEmail || '')
    const [site, setSite] = React.useState(website || '')
    const [saving, setSaving] = React.useState(false)

    React.useEffect(() => {
        setOfficial(!!isOfficial)
        setEmail(contactEmail || '')
        setSite(website || '')
    }, [isOfficial, contactEmail, website])

    const save = async (next) => {
        setSaving(true)
        const res = await updateOfficialAccount(userId, next)
        setSaving(false)
        if (res?.status === 200 && !res?.data?.errors?.length) {
            setSnackBar({
                status: true,
                text: res?.data?.message || 'Official account updated',
                severity: __snackBarSeverity.success
            })
            onSaved?.(next)
            return true
        }
        setSnackBar({
            status: true,
            text: res?.data?.errors?.[0]?.message || 'Unable to update the official account',
            severity: __snackBarSeverity.error
        })
        return false
    }

    const handleToggle = async (event) => {
        const checked = event.target.checked
        const nextEmail = checked ? (email || DEFAULT_CONTACT_EMAIL) : email
        const nextSite = checked ? (site || DEFAULT_WEBSITE) : site
        setOfficial(checked)
        setEmail(nextEmail)
        setSite(nextSite)
        const ok = await save({ isOfficial: checked, contactEmail: nextEmail, website: nextSite })
        if (!ok) setOfficial(!checked)
    }

    const handleSaveDetails = () => save({ isOfficial: official, contactEmail: email, website: site })

    const dirty = email !== (contactEmail || '') || site !== (website || '')

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: '220px' }}>
                <span style={{ opacity: official ? 1 : 0.6 }}>Official Nona account</span>
                <Switch size="small" checked={official} disabled={saving} onChange={handleToggle} sx={switchSx} />
            </div>
            <div style={{ opacity: 0.6, fontSize: '90%', marginBottom: '8px' }}>
                Shows an Official badge in the app, hides rating, children and provider reviews, and adds a Contact section.
            </div>
            {official && (
                <div style={{ display: 'flex', flexDirection: 'column', rowGap: '10px', marginTop: '6px' }}>
                    <TextField
                        size="small"
                        label="Contact email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={saving}
                    />
                    <TextField
                        size="small"
                        label="Website"
                        value={site}
                        onChange={(e) => setSite(e.target.value)}
                        disabled={saving}
                    />
                    <Button
                        variant="outlined"
                        size="small"
                        disabled={saving || !dirty}
                        onClick={handleSaveDetails}
                        sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                    >
                        Save contact details
                    </Button>
                </div>
            )}
        </>
    )
}

export default OfficialAccount
